import type { AdminApi, PrepareFailure } from '../api';
import { DEFAULT_PREPARE_LIMIT } from '../api';

export interface PrepareProgress {
  /** Words prepared since the run started. */
  prepared: number;
  /** Words still without a card as of the latest response. */
  remaining: number;
  /** Words whose preparation failed in the latest response of each word (by lemma). */
  failed: PrepareFailure[];
  /** Number of `prepare` calls made. */
  rounds: number;
}

export type PrepareStop =
  | 'done' // remaining reached 0
  | 'stalled' // a round prepared nothing; what is left keeps failing
  | 'cancelled'
  | 'error';

export interface PrepareOutcome extends PrepareProgress {
  stopped: PrepareStop;
  error?: unknown;
}

/**
 * Calls `prepare` again and again (the server does at most `limit` words per call) until nothing
 * is remaining, a round makes no progress (the rest fails every time), the caller cancels, or a
 * call throws. Failures are kept per word so the UI can list each one.
 */
export async function runPrepare(
  api: Pick<AdminApi, 'prepare'>,
  deckId: string,
  opts: {
    limit?: number;
    onProgress?: (p: PrepareProgress) => void;
    isCancelled?: () => boolean;
  } = {}
): Promise<PrepareOutcome> {
  const failures = new Map<string, PrepareFailure>();
  const progress: PrepareProgress = { prepared: 0, remaining: 0, failed: [], rounds: 0 };

  for (;;) {
    if (opts.isCancelled?.()) return { ...progress, stopped: 'cancelled' };
    let result;
    try {
      result = await api.prepare(deckId, opts.limit ?? DEFAULT_PREPARE_LIMIT);
    } catch (error) {
      return { ...progress, stopped: 'error', error };
    }
    progress.rounds += 1;
    progress.prepared += result.prepared;
    progress.remaining = result.remaining;
    for (const f of result.failed) failures.set(f.lemma, f);
    // Nothing remaining means every earlier failure was retried and succeeded.
    if (result.remaining <= 0) failures.clear();
    progress.failed = [...failures.values()];
    opts.onProgress?.({ ...progress });

    if (result.remaining <= 0) return { ...progress, stopped: 'done' };
    if (result.prepared <= 0) return { ...progress, stopped: 'stalled' };
  }
}
